<template>
    <div id="page-discussions" data-page="discussions" class="page-discussions py-2" :data-state="loadingInbox ? 'loading' : 'idle'">
        <!-- Sprint Selector Row with Integrated Project Space Picker -->
        <b-row class="my-2 align-items-center" v-if="hasConnectedPlatform">
            <b-col cols="12" class="d-flex justify-content-center">
                <picker-sprints
                    ref="sprintPicker"
                    connection-context="backlog"
                    show-all-time
                    @sprint-picked="onSprintPicked"
                    @sprints-loaded="onSprintsLoaded"
                />
            </b-col>
        </b-row>

        <!-- Missing Backlog Connection Warning Card -->
        <card-missing-source
            v-if="isMissingBacklogVisible"
            source-type="backlog"
            feature-name="Discussions"
            :project="globalProject"
        />

        <template v-else>
            <!-- Header Banner / Stats -->
            <grid-header-banner
                title="Discussions"
                subtitle="Work item and pull request threads mentioning you or awaiting your reply"
                icon="bi bi-chat-left-text-fill"
                icon-color="text-primary"
                :stats="discussionsStats"
            >
                <template #actions>
                    <b-button
                        size="sm"
                        variant="outline-secondary"
                        :disabled="loadingInbox || markingAll || summary.unreadCount === 0"
                        data-test="btn-mark-all-read"
                        aria-label="Mark all discussions as read"
                        title="Mark all discussions as read"
                        @click="markAllAsRead"
                    >
                        <b-spinner v-if="markingAll" small class="mr-1"></b-spinner>
                        <i v-else class="bi bi-check2-all mr-1"></i>
                        Mark All Read
                    </b-button>
                </template>
            </grid-header-banner>

        <!-- Grid Shell Container with Loading Overlay -->
        <b-overlay
            :show="loadingInbox"
            rounded="lg"
            opacity="0.6"
            class="discussions-grid-overlay"
            data-test="overlay-discussions-loading"
        >
            <template #overlay>
                <load-spinner text="Loading discussions..." />
            </template>

            <grid-shell
                class="inbox-grid"
                auto-fit-height
                :columns="visibleGridColumns"
                :custom-columns-handler="openColumnSettings"
                columns-button-test-id="btn-configure-columns"
            :loading="loadingInbox"
            :empty="!loadingInbox && displayedItems.length === 0"
            :has-active-filters="isAnyFilterActive"
            :empty-title="emptyTitle"
            :empty-message="emptyMessage"
            :empty-suggestions="emptySuggestions"
            @reset-filters="resetAllFilters"
        >
            <!-- Server-Side Filters & Client View Toolbar -->
            <template #filters>
                <!-- Filters Section (Server-side Criteria) -->
                <div class="d-flex align-items-center flex-wrap w-100">
                    <div class="filter-toolbar-label mr-2">
                        <i class="bi bi-funnel mr-1"></i><strong>Filters:</strong>
                    </div>

                    <!-- Source Filter -->
                    <b-dropdown
                        size="sm"
                        :variant="draftSource !== 'All' ? 'primary' : 'outline-secondary'"
                        no-caret
                        class="grid-filter-chip mr-2"
                        data-test="filter-dropdown-source"
                    >
                        <template #button-content>
                            <span class="font-weight-bold mr-1">Source:</span>
                            <span>{{ sourceLabel(draftSource) }}</span>
                            <i class="bi bi-chevron-down ml-1 filter-chevron"></i>
                        </template>
                        <b-dropdown-item-button :active="draftSource === 'All'" @click="draftSource = 'All'">
                            All Discussions
                        </b-dropdown-item-button>
                        <b-dropdown-item-button :active="draftSource === 'Mention'" @click="draftSource = 'Mention'">
                            Mentioned Me
                        </b-dropdown-item-button>
                        <b-dropdown-item-button :active="draftSource === 'Assigned'" @click="draftSource = 'Assigned'">
                            Assigned to Me
                        </b-dropdown-item-button>
                    </b-dropdown>

                    <!-- Lookback Window Filter -->
                    <b-dropdown
                        size="sm"
                        :variant="draftDays !== 14 ? 'primary' : 'outline-secondary'"
                        no-caret
                        class="grid-filter-chip mr-2"
                        data-test="filter-dropdown-days"
                    >
                        <template #button-content>
                            <span class="font-weight-bold mr-1">Period:</span>
                            <span>{{ draftDays }} Days</span>
                            <i class="bi bi-chevron-down ml-1 filter-chevron"></i>
                        </template>
                        <b-dropdown-item-button :active="draftDays === 7" @click="draftDays = 7">
                            Past 7 Days
                        </b-dropdown-item-button>
                        <b-dropdown-item-button :active="draftDays === 14" @click="draftDays = 14">
                            Past 14 Days (Default)
                        </b-dropdown-item-button>
                        <b-dropdown-item-button :active="draftDays === 30" @click="draftDays = 30">
                            Past 30 Days (Max)
                        </b-dropdown-item-button>
                    </b-dropdown>

                    <!-- Action Buttons aligned to the right -->
                    <div class="filter-toolbar-actions ml-auto d-flex align-items-center">
                        <!-- Apply Filters Button -->
                    <b-button
                        size="sm"
                        variant="primary"
                        class="mr-2"
                        :class="{ 'btn-apply-pulse': hasUnappliedFilters && !loadingInbox }"
                        :disabled="loadingInbox || !hasUnappliedFilters"
                        data-test="btn-apply-filters"
                        :data-dirty="hasUnappliedFilters ? 'true' : 'false'"
                        :data-state="loadingInbox ? 'loading' : 'idle'"
                        aria-label="Apply server-side filters"
                        title="Apply server-side filters"
                        @click="applyFilters"
                    >
                        <i class="bi bi-check-circle mr-1"></i>Apply
                    </b-button>

                    <!-- Reset to Defaults Button -->
                    <b-button
                        class="mr-0"
                        size="sm"
                        variant="outline-secondary"
                        :disabled="loadingInbox"
                        data-test="btn-reset-filters"
                        aria-label="Reset all filters to defaults"
                        title="Reset all filters to defaults"
                        @click="resetAllFilters"
                    >
                        <i class="bi bi-arrow-counterclockwise mr-1"></i>Reset to Defaults
                    </b-button>
                </div>

                    </div>
            </template>
            <template #view>
                <!-- Source Type Filter Pills (All | Work Items | Pull Requests) -->
                <div class="d-inline-flex align-items-center mr-2 discussions-source-pills" data-test="filter-discussions-source">
                    <span class="extra-small text-muted font-weight-bold mr-1">Source:</span>
                    <b-button-group size="sm">
                        <b-button
                            :variant="sourceTypeFilter === 'All' ? 'primary' : 'outline-secondary'"
                            data-test="filter-source-all"
                            @click="sourceTypeFilter = 'All'"
                        >
                            All
                        </b-button>
                        <b-button
                            :variant="sourceTypeFilter === 'WorkItem' ? 'primary' : 'outline-secondary'"
                            data-test="filter-source-workitems"
                            @click="sourceTypeFilter = 'WorkItem'"
                        >
                            <i class="bi bi-kanban mr-1"></i>Work Items
                        </b-button>
                        <b-button
                            :variant="sourceTypeFilter === 'PullRequest' ? 'primary' : 'outline-secondary'"
                            data-test="filter-source-prs"
                            @click="sourceTypeFilter = 'PullRequest'"
                        >
                            <i class="bi bi-git mr-1"></i>Pull Requests
                        </b-button>
                    </b-button-group>
                </div>
                <!-- Instant Search Input -->
                <b-input-group size="sm" class="mr-2 inbox-search-input">
                    <b-input-group-prepend is-text>
                        <i class="bi bi-search"></i>
                    </b-input-group-prepend>
                    <b-form-input
                        v-model="searchQuery"
                        placeholder="Search discussions..."
                        data-test="input-inbox-search"
                        aria-label="Search discussions"
                    />
                    <b-input-group-append v-if="searchQuery">
                        <b-button variant="outline-secondary" aria-label="Clear search" title="Clear" @click="searchQuery = ''">
                            <i class="bi bi-x"></i>
                        </b-button>
                    </b-input-group-append>
                </b-input-group>

                <!-- Toggle: Awaiting Answer -->
                <b-button
                    size="sm"
                    :variant="unansweredOnly ? 'danger' : 'outline-secondary'"
                    class="mr-2"
                    data-test="btn-toggle-unanswered"
                    aria-label="Filter discussions awaiting your answer"
                    title="Filter discussions awaiting your answer"
                    @click="unansweredOnly = !unansweredOnly"
                >
                    <i :class="unansweredOnly ? 'bi bi-funnel-fill mr-1' : 'bi bi-funnel mr-1'"></i>
                    Awaiting Answer
                </b-button>

                <!-- Toggle: Unread -->
                <b-button
                    size="sm"
                    :variant="unreadOnly ? 'primary' : 'outline-secondary'"
                    data-test="btn-toggle-unread"
                    aria-label="Filter unread discussions"
                    title="Filter unread discussions"
                    @click="unreadOnly = !unreadOnly"
                >
                    <i :class="unreadOnly ? 'bi bi-funnel-fill mr-1' : 'bi bi-funnel mr-1'"></i>
                    Unread
                </b-button>
            </template>
            <template v-if="false">
                <div class="d-flex align-items-center flex-wrap ml-auto py-1">
                    <div class="filter-toolbar-label mr-2">
                        <i class="bi bi-eye mr-1"></i><strong>View:</strong>
                    </div>

                    <!-- Instant Search Input -->
                    <b-input-group size="sm" class="mr-2 inbox-search-input">
                        <b-input-group-prepend is-text>
                            <i class="bi bi-search"></i>
                        </b-input-group-prepend>
                        <b-form-input
                            v-model="searchQuery"
                            placeholder="Search discussions..."
                            data-test="input-inbox-search"
                            aria-label="Search discussions"
                        />
                        <b-input-group-append v-if="searchQuery">
                            <b-button variant="outline-secondary" aria-label="Clear search" title="Clear" @click="searchQuery = ''">
                                <i class="bi bi-x"></i>
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>

                    <!-- Toggle: Awaiting Answer -->
                    <b-button
                        size="sm"
                        :variant="unansweredOnly ? 'danger' : 'outline-secondary'"
                        class="mr-2"
                        data-test="btn-toggle-unanswered"
                        aria-label="Filter discussions awaiting your answer"
                        title="Filter discussions awaiting your answer"
                        @click="unansweredOnly = !unansweredOnly"
                    >
                        <i :class="unansweredOnly ? 'bi bi-funnel-fill mr-1' : 'bi bi-funnel mr-1'"></i>
                        Awaiting Answer
                    </b-button>

                    <!-- Toggle: Unread -->
                    <b-button
                        size="sm"
                        :variant="unreadOnly ? 'primary' : 'outline-secondary'"
                        data-test="btn-toggle-unread"
                        aria-label="Filter unread discussions"
                        title="Filter unread discussions"
                        @click="unreadOnly = !unreadOnly"
                    >
                        <i :class="unreadOnly ? 'bi bi-funnel-fill mr-1' : 'bi bi-funnel mr-1'"></i>
                        Unread
                    </b-button>
                </div>
            </template>

            <!-- Grid Rows -->
            <div class="inbox-rows-container">
                <b-card
                    v-for="item in displayedItems"
                    :key="item.workItemId"
                    class="mb-2 inbox-item-card"
                    :class="{ 'is-unread': item.unreadCount > 0, 'needs-reply': (!discussionsFeatureSettings || (discussionsFeatureSettings.enabled !== false && discussionsFeatureSettings.highlightAwaitingAnswer !== false)) && item.discussionStatus === 'AwaitingAnswer' }"
                    data-test="grid-row"
                    :data-test-id="item.workItemId"
                >
                    <b-row class="my-1 grid-row align-items-center">
                        <template v-for="col in visibleGridColumns">
                        <!-- Col 1: ID & Work Item Type (nav-link-button) -->
                        <b-col v-if="col.key === 'id'" :key="col.key" class="px-2 grid-col-id d-flex align-items-center justify-content-center text-center">
                            <nav-link-button
                                :href="item.url"
                                :id="item.workItemId"
                                :type-icon="item.workItemTypeIcon"
                                :work-item-type="item.workItemType"
                                :is-pr="item.sourceType === 'PullRequest'"
                                :title="item.sourceType === 'PullRequest' ? 'Open pull request in Azure DevOps' : 'Open work item in Azure DevOps'"
                                data-test="btn-workitem-link"
                            />
                        </b-col>

                        <!-- Col 2: Title & Badges -->
                        <b-col v-else-if="col.key === 'title'" :key="col.key" class="px-2 grid-col-title d-flex flex-column justify-content-center overflow-hidden" style="min-width: 0;">
                            <div class="font-weight-bold text-truncate" :title="item.title">
                                {{ item.title }}
                            </div>
                            <div class="d-flex align-items-center flex-wrap mt-1">
                                <status-badge :status="item.state" class="mr-1" />
                                <b-badge
                                    v-if="item.sourceType === 'PullRequest' && item.repositoryName"
                                    variant="light"
                                    class="mr-1 extra-small border badge-pr-repo"
                                    data-test="badge-pr-repo"
                                >
                                    <i class="bi bi-git mr-1 text-primary"></i>{{ item.repositoryName }}
                                </b-badge>
                                <b-badge
                                    v-if="item.sourceType === 'PullRequest' && item.threadStatus"
                                    :variant="item.threadStatus === 'Active' ? 'warning' : 'success'"
                                    class="mr-1 extra-small font-weight-bold"
                                    data-test="badge-pr-thread-status"
                                >
                                    <i :class="item.threadStatus === 'Active' ? 'bi bi-chat-dots-fill mr-1' : 'bi bi-check2-circle mr-1'"></i>{{ item.threadStatus }}
                                </b-badge>
                                <b-badge
                                    v-if="item.sourceType === 'PullRequest' && item.filePath"
                                    variant="light"
                                    class="mr-1 extra-small text-monospace border badge-pr-file-path"
                                    :title="item.filePath"
                                    data-test="badge-pr-file-path"
                                >
                                    <i class="bi bi-file-earmark-code mr-1"></i>{{ formatFileLeaf(item.filePath) }}
                                </b-badge>
                                <!-- Reason Badges -->
                                <b-badge v-if="(!discussionsFeatureSettings || (discussionsFeatureSettings.enabled !== false && discussionsFeatureSettings.showReasonBadges !== false)) && (item.reason === 'Both' || item.reason === 'Mention')" variant="warning" class="mr-1 extra-small font-weight-bold badge-mention" data-test="badge-mention">
                                    <i class="bi bi-at mr-1"></i>Mention
                                </b-badge>
                                <b-badge v-if="(!discussionsFeatureSettings || (discussionsFeatureSettings.enabled !== false && discussionsFeatureSettings.showReasonBadges !== false)) && (item.reason === 'Both' || item.reason === 'Assigned')" variant="info" class="mr-1 extra-small">
                                    <i class="bi bi-person mr-1"></i>Assigned
                                </b-badge>
                                <b-badge v-if="!discussionsFeatureSettings || (discussionsFeatureSettings.enabled !== false && discussionsFeatureSettings.showReasonBadges !== false)" variant="light" class="extra-small text-muted border">
                                    <i class="bi bi-chat-left-text mr-1"></i>{{ item.commentCount }}
                                </b-badge>
                            </div>
                        </b-col>

                        <!-- Col 3: Status -->
                        <b-col v-else-if="col.key === 'status'" :key="col.key" class="px-2 grid-col-status d-flex align-items-center justify-content-center text-center">
                            <div class="d-flex align-items-center flex-wrap justify-content-center">
                                <b-badge
                                    v-if="item.discussionStatus === 'AwaitingAnswer'"
                                    variant="danger"
                                    class="py-1 pl-2 pr-1 font-weight-bold reply-status-badge d-inline-flex align-items-center"
                                    :style="discussionStatusStyle('AwaitingAnswer')"
                                    data-test="badge-awaiting-answer"
                                >
                                    <i class="bi bi-exclamation-circle-fill mr-1"></i>Awaiting Answer
                                    <button
                                        type="button"
                                        class="badge-dismiss-btn ml-1"
                                        title="Dismiss label (answered in person or meeting)"
                                        aria-label="Dismiss label"
                                        data-test="btn-dismiss-status"
                                        @click.stop.prevent="dismissStatus(item)"
                                    >
                                        <i class="bi bi-x"></i>
                                    </button>
                                </b-badge>
                                <b-badge
                                    v-else-if="item.discussionStatus === 'AwaitingReply'"
                                    variant="warning"
                                    class="py-1 pl-2 pr-1 font-weight-bold reply-status-badge d-inline-flex align-items-center"
                                    :style="discussionStatusStyle('AwaitingReply')"
                                    data-test="badge-awaiting-reply"
                                >
                                    <i class="bi bi-hourglass-split mr-1"></i>Awaiting Reply
                                    <button
                                        type="button"
                                        class="badge-dismiss-btn ml-1"
                                        title="Dismiss label (replied in person or meeting)"
                                        aria-label="Dismiss label"
                                        data-test="btn-dismiss-status"
                                        @click.stop.prevent="dismissStatus(item)"
                                    >
                                        <i class="bi bi-x"></i>
                                    </button>
                                </b-badge>
                                <b-badge
                                    v-else-if="item.discussionStatus === 'Replied'"
                                    variant="success"
                                    class="py-1 pl-2 pr-1 font-weight-bold reply-status-badge d-inline-flex align-items-center"
                                    :style="discussionStatusStyle('Replied')"
                                    data-test="badge-replied"
                                >
                                    <i class="bi bi-check-circle-fill mr-1"></i>Replied
                                    <button
                                        type="button"
                                        class="badge-dismiss-btn ml-1"
                                        title="Dismiss label"
                                        aria-label="Dismiss label"
                                        data-test="btn-dismiss-status"
                                        @click.stop.prevent="dismissStatus(item)"
                                    >
                                        <i class="bi bi-x"></i>
                                    </button>
                                </b-badge>
                                <b-badge
                                    v-else-if="item.discussionStatus === 'Answered'"
                                    variant="success"
                                    class="py-1 pl-2 pr-1 font-weight-bold reply-status-badge d-inline-flex align-items-center"
                                    :style="discussionStatusStyle('Answered')"
                                    data-test="badge-answered"
                                >
                                    <i class="bi bi-check-circle-fill mr-1"></i>Answered
                                    <button
                                        type="button"
                                        class="badge-dismiss-btn ml-1"
                                        title="Dismiss label"
                                        aria-label="Dismiss label"
                                        data-test="btn-dismiss-status"
                                        @click.stop.prevent="dismissStatus(item)"
                                    >
                                        <i class="bi bi-x"></i>
                                    </button>
                                </b-badge>
                                <!-- When NotActionable: empty / no badge -->

                                <!-- Closed envelope icon next to status when unread -->
                                <i
                                    v-if="(!discussionsFeatureSettings || (discussionsFeatureSettings.enabled !== false && discussionsFeatureSettings.showUnreadEnvelope !== false)) && item.unreadCount > 0"
                                    class="bi bi-envelope-fill text-primary ml-2 unread-envelope-icon"
                                    data-test="icon-unread-envelope"
                                    v-b-tooltip.hover="'Unread discussion'"
                                    aria-label="Unread discussion"
                                ></i>
                            </div>
                        </b-col>

                        <!-- Col 4: Latest Discussion -->
                        <b-col v-else-if="col.key === 'latestComment'" :key="col.key" class="px-2 grid-col-latestComment d-flex flex-column justify-content-center overflow-hidden" style="min-width: 0;">
                            <div class="mobile-col-caption d-lg-none">Latest Discussion</div>
                            <div class="latest-comment-box p-2 rounded">
                                <div class="d-flex align-items-center justify-content-between mb-1">
                                    <strong class="extra-small text-truncate mr-2 text-body">
                                        {{ item.latestComment ? item.latestComment.authorName : 'Discussion' }}
                                    </strong>
                                    <span v-if="item.latestComment" class="extra-small text-muted flex-shrink-0">
                                        {{ formatRelative(item.latestComment.createdDate) }}
                                    </span>
                                </div>
                                <div class="comment-excerpt small text-truncate" :title="item.latestComment ? item.latestComment.text : ''">
                                    {{ formatExcerpt(item.latestComment ? item.latestComment.text : 'No comments yet') }}
                                </div>
                            </div>
                        </b-col>

                        <!-- Col 5: Actions -->
                        <!-- Optional Col: Trigger / Reason -->
                        <b-col v-else-if="col.key === 'reason'" :key="col.key" class="px-2 grid-col-reason d-flex align-items-center justify-content-center text-center">
                            <b-badge v-if="item.reason === 'Both' || item.reason === 'Mention'" variant="warning" class="mr-1 extra-small font-weight-bold badge-mention">
                                <i class="bi bi-at mr-1"></i>Mention
                            </b-badge>
                            <b-badge v-if="item.reason === 'Both' || item.reason === 'Assigned'" variant="info" class="extra-small">
                                <i class="bi bi-person mr-1"></i>Assigned
                            </b-badge>
                        </b-col>

                        <!-- Optional Col: Sprint / Iteration -->
                        <b-col v-else-if="col.key === 'iteration'" :key="col.key" class="px-2 grid-col-iteration d-flex align-items-center justify-content-center text-center">
                            <span class="extra-small text-muted text-truncate" :title="item.iterationPath || ''">
                                {{ formatIterationLeaf(item.iterationPath) }}
                            </span>
                        </b-col>

                        <!-- Optional Col: Last Activity -->
                        <b-col v-else-if="col.key === 'lastActivity'" :key="col.key" class="px-2 grid-col-lastActivity d-flex align-items-center justify-content-center text-center">
                            <span class="extra-small text-muted">
                                {{ formatRelative(item.lastActivityUtc || (item.latestComment && item.latestComment.createdDate)) }}
                            </span>
                        </b-col>

                        <!-- Col 5: Actions -->
                        <b-col v-else-if="col.key === 'actions'" :key="col.key" class="px-1 grid-col-actions d-flex align-items-center justify-content-center text-center">
                            <b-button
                                size="sm"
                                variant="primary"
                                class="py-1 px-2"
                                aria-label="Open discussion"
                                title="Open discussion"
                                data-test="btn-open-discussion"
                                @click="openDiscussion(item)"
                            >
                                <i class="bi bi-chat-left-dots mr-1"></i>Discuss
                            </b-button>
                        </b-col>
                        </template>
                        <b-col v-if="false"><b-button v-if="false">
                            </b-button>
                        </b-col>
                    </b-row>
                </b-card>
            </div>
        </grid-shell>
        </b-overlay>
        </template>

        <!-- Comments Modal -->
        <!-- Comments Modal -->
        <modal-task-columns
            ref="modalDiscussionsColumns"
            modal-id="modal-discussions-columns"
            modal-title="Customize Discussions Columns & Attention Setup"
            subtitle="Drag and drop column badges below to customize their display sequence in the Discussions grid. Use the eye icon to show or hide columns, or the palette icon on Status to customize discussion status colors. The Actions column always remains fixed at the end."
            :allow-custom-fields="false"
            storage-key="vt_discussions_columns_config"
            fixed-action-ref="actions"
            :default-columns="defaultDiscussionsColumns"
            :current-columns="activeGridColumns"
            :feature-config="discussionsFeatureConfig"
            @feature-settings-saved="onDiscussionsFeatureSettingsSaved"
            @columns-saved="onColumnsSaved"
        />

        <!-- Inline Pull Request Comments Modal -->
        <b-modal
            v-model="showPrCommentsModal"
            id="modal-pr-comments"
            data-test="modal-pr-comments"
            size="lg"
            scrollable
            hide-footer
            dialog-class="modal-dialog-elevated"
        >
            <template #modal-title>
                <div class="d-flex align-items-center flex-wrap" v-if="selectedPrDiscussion">
                    <i class="bi bi-git text-primary mr-2"></i>
                    <span class="font-weight-bold mr-2">PR !{{ selectedPrDiscussion.pullRequestId || selectedPrDiscussion.workItemId }}</span>
                    <span class="text-muted small text-truncate" style="max-width: 440px;">{{ selectedPrDiscussion.title }}</span>
                    <b-badge v-if="selectedPrDiscussion.repositoryName" variant="light" class="ml-2 extra-small border">
                        <i class="bi bi-hdd-network mr-1"></i>{{ selectedPrDiscussion.repositoryName }}
                    </b-badge>
                </div>
            </template>

            <div v-if="loadingPrComments" class="text-center py-4">
                <b-spinner variant="primary" small class="mr-2"></b-spinner>
                <span class="text-muted small">Loading pull request review threads...</span>
            </div>

            <div v-else-if="prCommentThreads.length === 0" class="text-center py-4 text-muted small" data-test="pr-comments-empty">
                <i class="bi bi-chat-square-dots d-block mb-2" style="font-size: 1.5rem; opacity: 0.5;"></i>
                No active comment threads found for this pull request.
            </div>

            <div v-else class="pr-comments-list" data-test="pr-comments-list">
                <div
                    v-for="thread in prCommentThreads"
                    :key="thread.threadId"
                    class="pr-comment-thread p-3 mb-2 rounded border"
                    :class="{ 'pr-comment-thread-active': thread.isActive }"
                    data-test="pr-comment-thread"
                    :data-thread-id="thread.threadId"
                >
                    <div class="d-flex align-items-center justify-content-between mb-1 flex-wrap">
                        <div class="d-flex align-items-center flex-wrap">
                            <strong class="small mr-2">{{ thread.authorName }}</strong>
                            <b-badge
                                :variant="prThreadStatusVariant(thread.status)"
                                pill
                                class="extra-small"
                                data-test="pr-thread-status-badge"
                            >
                                {{ thread.status }}
                            </b-badge>
                            <b-badge
                                v-if="thread.filePath"
                                variant="light"
                                class="ml-2 extra-small text-monospace border badge-pr-file-path"
                                :title="thread.filePath"
                                data-test="pr-thread-file-path"
                            >
                                <i class="bi bi-file-earmark-code mr-1"></i>{{ thread.filePath }}
                            </b-badge>
                        </div>
                        <div class="d-flex align-items-center">
                            <span class="text-muted extra-small mr-2">{{ formatPrCommentDate(thread.publishedDate) }}</span>
                            <template v-if="thread.allowedTransitions && thread.allowedTransitions.length > 0">
                                <b-button
                                    v-for="transition in thread.allowedTransitions"
                                    :key="transition.actionKey"
                                    size="sm"
                                    :variant="getPrThreadActionVariant(transition)"
                                    class="py-0 px-2 extra-small ml-1"
                                    :disabled="updatingPrStatusThreadId === thread.threadId"
                                    :data-test="`btn-pr-thread-action-${transition.actionKey}`"
                                    @click="changePrThreadStatus(thread, transition)"
                                >
                                    <b-spinner v-if="updatingPrStatusThreadId === thread.threadId" small class="mr-1"></b-spinner>
                                    {{ transition.label }}
                                </b-button>
                            </template>
                        </div>
                    </div>

                    <div v-if="editingPrReplyKey === `${thread.threadId}-${thread.commentId || 1}`" class="mt-2">
                        <b-form-textarea
                            v-model="editingPrReplyText"
                            rows="2"
                            size="sm"
                            class="mb-1"
                            data-test="input-edit-pr-comment"
                        ></b-form-textarea>
                        <div class="d-flex justify-content-end">
                            <b-button size="sm" variant="link" class="py-0 px-2 extra-small text-muted mr-1" @click="cancelEditPrReply">Cancel</b-button>
                            <b-button
                                size="sm"
                                variant="primary"
                                class="py-0 px-2 extra-small"
                                :disabled="savingPrReplyKey === `${thread.threadId}-${thread.commentId || 1}` || !editingPrReplyText.trim()"
                                data-test="btn-save-edit-pr-comment"
                                @click="saveEditPrReply(thread, thread.commentId || 1, true)"
                            >Save</b-button>
                        </div>
                    </div>
                    <div v-else class="d-flex align-items-start justify-content-between">
                        <div class="small text-break pr-comment-body flex-grow-1">{{ thread.text }}</div>
                        <div v-if="canMutatePrComment(thread.authorName)" class="ml-2 flex-shrink-0 d-flex align-items-center">
                            <b-button
                                size="sm"
                                variant="link"
                                class="p-0 text-muted mr-2 extra-small"
                                title="Edit comment"
                                aria-label="Edit comment"
                                data-test="btn-edit-pr-root-comment"
                                @click="startEditPrReply(thread.threadId, thread.commentId || 1, thread.text)"
                            ><i class="bi bi-pencil"></i></b-button>
                            <b-button
                                size="sm"
                                variant="link"
                                class="p-0 text-danger extra-small"
                                title="Delete comment"
                                aria-label="Delete comment"
                                :disabled="deletingPrReplyKey === `${thread.threadId}-${thread.commentId || 1}`"
                                data-test="btn-delete-pr-root-comment"
                                @click="deletePrReply(thread, thread.commentId || 1)"
                            ><i class="bi bi-trash"></i></b-button>
                        </div>
                    </div>

                    <!-- Existing Replies -->
                    <div v-if="thread.replies && thread.replies.length > 0" class="pr-comment-replies mt-2 pl-3 border-left">
                        <div
                            v-for="reply in thread.replies"
                            :key="reply.commentId"
                            class="pr-comment-reply py-1 mb-1"
                            data-test="pr-comment-reply"
                        >
                            <div class="d-flex align-items-center justify-content-between">
                                <strong class="extra-small">{{ reply.authorName }}</strong>
                                <div class="d-flex align-items-center">
                                    <span class="text-muted extra-small mr-2">{{ formatPrCommentDate(reply.publishedDate) }}</span>
                                    <template v-if="canMutatePrComment(reply.authorName)">
                                        <b-button
                                            size="sm"
                                            variant="link"
                                            class="p-0 text-muted mr-2 extra-small"
                                            title="Edit reply"
                                            aria-label="Edit reply"
                                            data-test="btn-edit-pr-reply"
                                            @click="startEditPrReply(thread.threadId, reply.commentId, reply.text)"
                                        ><i class="bi bi-pencil"></i></b-button>
                                        <b-button
                                            size="sm"
                                            variant="link"
                                            class="p-0 text-danger extra-small"
                                            title="Delete reply"
                                            aria-label="Delete reply"
                                            :disabled="deletingPrReplyKey === `${thread.threadId}-${reply.commentId}`"
                                            data-test="btn-delete-pr-reply"
                                            @click="deletePrReply(thread, reply.commentId)"
                                        ><i class="bi bi-trash"></i></b-button>
                                    </template>
                                </div>
                            </div>
                            <div v-if="editingPrReplyKey === `${thread.threadId}-${reply.commentId}`" class="mt-1">
                                <b-form-textarea
                                    v-model="editingPrReplyText"
                                    rows="2"
                                    size="sm"
                                    class="mb-1"
                                    data-test="input-edit-pr-reply"
                                ></b-form-textarea>
                                <div class="d-flex justify-content-end">
                                    <b-button size="sm" variant="link" class="py-0 px-2 extra-small text-muted mr-1" @click="cancelEditPrReply">Cancel</b-button>
                                    <b-button
                                        size="sm"
                                        variant="primary"
                                        class="py-0 px-2 extra-small"
                                        :disabled="savingPrReplyKey === `${thread.threadId}-${reply.commentId}` || !editingPrReplyText.trim()"
                                        data-test="btn-save-edit-pr-reply"
                                        @click="saveEditPrReply(thread, reply.commentId, false)"
                                    >Save</b-button>
                                </div>
                            </div>
                            <div v-else class="small text-break">{{ reply.text }}</div>
                        </div>
                    </div>

                    <!-- Inline Reply Input -->
                    <div class="pr-thread-reply-box mt-2 pt-2 border-top d-flex align-items-center">
                        <b-form-input
                            v-model="prReplyDrafts[thread.threadId]"
                            size="sm"
                            placeholder="Write a reply..."
                            class="mr-2"
                            :disabled="submittingPrReplyThreadId === thread.threadId"
                            :data-test="`input-pr-reply-${thread.threadId}`"
                            @keyup.enter="submitPrReply(thread)"
                        ></b-form-input>
                        <b-button
                            size="sm"
                            variant="primary"
                            class="flex-shrink-0"
                            :disabled="submittingPrReplyThreadId === thread.threadId || !(prReplyDrafts[thread.threadId] && prReplyDrafts[thread.threadId].trim())"
                            :data-test="`btn-submit-pr-reply-${thread.threadId}`"
                            @click="submitPrReply(thread)"
                        >
                            <b-spinner v-if="submittingPrReplyThreadId === thread.threadId" small class="mr-1"></b-spinner>
                            <i v-else class="bi bi-reply-fill mr-1"></i>Reply
                        </b-button>
                    </div>
                </div>
            </div>
        </b-modal>

        <modal-work-item-comments
            ref="commentsModal"
            @comments-loaded="onCommentsLoaded"
            @comment-added="onCommentAdded"
            @comment-deleted="onCommentDeleted"
            @comments-updated="onCommentsUpdated"
            @status-dismissed="onStatusDismissed"
        />
    </div>
</template>

<script>
import CardMissingSource from './card-missing-source.vue';
import GridShell from './grid-shell.vue';
import ModalWorkItemComments from './modal-work-item-comments.vue';
import NavLinkButton from './nav-link-button.vue';
import PickerSprints from './picker-sprints.vue';
import StatusBadge from './status-badge.vue';
import GridHeaderBanner from './grid-header-banner.vue';
import ModalTaskColumns from './modal-task-columns.vue';

const DEFAULT_DISCUSSION_STATUS_COLORS = {
    AwaitingAnswer: '#ef4444',
    AwaitingReply: '#f59e0b',
    Replied: '#10b981',
    Answered: '#10b981'
};

const DEFAULT_DISCUSSIONS_COLUMNS = [
    { key: 'id', referenceName: 'id', label: '#', headerClass: 'text-center', width: '6.25rem', isSystem: true, order: 1, isVisible: true, hidden: false },
    { key: 'title', referenceName: 'title', label: 'Work Item', headerClass: 'text-left', width: 'minmax(18rem, 2.5fr)', isSystem: true, order: 2, isVisible: true, hidden: false },
    {
        key: 'status',
        referenceName: 'status',
        label: 'Status',
        headerClass: 'text-center',
        width: '9.75rem',
        isSystem: true,
        order: 3,
        isVisible: true,
        hidden: false,
        allowedValues: ['AwaitingAnswer', 'AwaitingReply', 'Replied', 'Answered'],
        valueColors: { ...DEFAULT_DISCUSSION_STATUS_COLORS }
    },
    { key: 'latestComment', referenceName: 'latestComment', label: 'Latest Discussion', headerClass: 'text-left', width: 'minmax(13rem, 1.2fr)', isSystem: true, order: 4, isVisible: true, hidden: false },
    { key: 'reason', referenceName: 'reason', label: 'Trigger', headerClass: 'text-center', width: '7rem', isSystem: true, order: 5, isVisible: false, hidden: true },
    { key: 'iteration', referenceName: 'iteration', label: 'Sprint', headerClass: 'text-center', width: '9rem', isSystem: true, order: 6, isVisible: false, hidden: true },
    { key: 'lastActivity', referenceName: 'lastActivity', label: 'Last Activity', headerClass: 'text-center', width: '7.5rem', isSystem: true, order: 7, isVisible: false, hidden: true },
    { key: 'actions', referenceName: 'actions', label: 'Actions', headerClass: 'text-center', width: '6.5rem', isSystem: true, order: 8, isVisible: true, hidden: false }
];
import mixinToast from '../mixinToast';
import commonMixin from '../mixinCommon';
import accountMixin from '../mixinAccount';

const MOCK_DISCUSSIONS = [
    {
        sourceType: "PullRequest",
        workItemId: 2088,
        pullRequestId: 2088,
        repositoryId: "repo-core",
        repositoryName: "velocytrack-core",
        filePath: "/src/components/page-config.vue",
        threadStatus: "Active",
        threadId: 1,
        title: "Fix dark mode badge contrast in Config page",
        workItemType: "Pull Request",
        workItemTypeIcon: "pr",
        state: "Active",
        iterationPath: "VelocyTrack\\Sprint 42",
        url: "https://dev.azure.com/velocytrack/core/_git/velocytrack-core/pullrequest/2088",
        projectName: "VelocyTrack-Engineering",
        organization: "Demo",
        reason: "Both",
        commentCount: 2,
        mentionCount: 1,
        unreadCount: 1,
        isAnswered: false,
        discussionStatus: "AwaitingAnswer",
        latestComment: {
            id: 1,
            workItemId: 2088,
            authorName: "Sarah Connor",
            createdDate: new Date(Date.now() - 3600000 * 3).toISOString(),
            text: "@Alex Morgan Looks good overall! Could we verify the WCAG AA contrast ratio on `.badge-soft-warning` in dark mode before merging?",
            mentionsMe: true,
            isFromCurrentUser: false
        },
        lastActivityUtc: new Date(Date.now() - 3600000 * 3).toISOString()
    },
    {
        sourceType: "PullRequest",
        workItemId: 2094,
        pullRequestId: 2094,
        repositoryId: "repo-core",
        repositoryName: "velocytrack-core",
        filePath: "/src/components/chart-sprint-burndown.vue",
        threadStatus: "Active",
        threadId: 1,
        title: "Optimize sprint burndown chart rendering for large teams",
        workItemType: "Pull Request",
        workItemTypeIcon: "pr",
        state: "Active",
        iterationPath: "VelocyTrack\\Sprint 42",
        url: "https://dev.azure.com/velocytrack/core/_git/velocytrack-core/pullrequest/2094",
        projectName: "VelocyTrack-Engineering",
        organization: "Demo",
        reason: "Mention",
        commentCount: 2,
        mentionCount: 1,
        unreadCount: 1,
        isAnswered: false,
        discussionStatus: "AwaitingAnswer",
        latestComment: {
            id: 1,
            workItemId: 2094,
            authorName: "Elena Rostova",
            createdDate: new Date(Date.now() - 3600000 * 4).toISOString(),
            text: "@Alex Morgan Since you optimized the canvas dataset decimation in Sprint 13, does this 60fps requestAnimationFrame throttle look safe for 500+ points?",
            mentionsMe: true,
            isFromCurrentUser: false
        },
        lastActivityUtc: new Date(Date.now() - 3600000 * 4).toISOString()
    },
    {
        workItemId: 211,
        title: "Setup SignalR hub for real-time telemetry streaming",
        workItemType: "Task",
        workItemTypeIcon: "task",
        state: "In Progress",
        iterationPath: "VelocyTrack\\Sprint 42",
        url: "https://dev.azure.com/velocytrack/core/_workitems/edit/211",
        projectName: "VelocyTrack-Engineering",
        organization: "Demo",
        reason: "Both",
        commentCount: 4,
        mentionCount: 2,
        unreadCount: 1,
        isAnswered: false,
        discussionStatus: "AwaitingAnswer",
        latestComment: {
            id: 3,
            workItemId: 211,
            authorName: "Sarah Chen",
            authorEmail: "sarah.chen@demo.velocytrack.com",
            createdDate: new Date(Date.now() - 3600000 * 2).toISOString(),
            text: "@Alex Morgan Hub route configured at `/telemetry/stream`. Make sure cookie auth exchange is enabled on the WebSocket upgrade request.",
            mentionsMe: true,
            isFromCurrentUser: false
        },
        lastActivityUtc: new Date(Date.now() - 3600000 * 2).toISOString()
    },
    {
        workItemId: 230,
        title: "Sprint Burndown & Velocity Analytics Engine",
        workItemType: "User Story",
        workItemTypeIcon: "story",
        state: "In Progress",
        iterationPath: "VelocyTrack\\Sprint 42",
        url: "https://dev.azure.com/velocytrack/core/_workitems/edit/230",
        projectName: "VelocyTrack-Engineering",
        organization: "Demo",
        reason: "Both",
        commentCount: 3,
        mentionCount: 1,
        unreadCount: 1,
        isAnswered: false,
        discussionStatus: "AwaitingAnswer",
        latestComment: {
            id: 8,
            workItemId: 230,
            authorName: "Dzhem Hasan",
            authorEmail: "jem.hasan@nuvei.com",
            createdDate: new Date(Date.now() - 3600000 * 5).toISOString(),
            text: "@Alex Morgan Ensure non-working days (weekends and national holidays) do not negatively bias the ideal burndown slope.",
            mentionsMe: true,
            isFromCurrentUser: false
        },
        lastActivityUtc: new Date(Date.now() - 3600000 * 5).toISOString()
    },
    {
        workItemId: 344,
        title: "Deploy Redis Sentinel topology in staging environment",
        workItemType: "Task",
        workItemTypeIcon: "task",
        state: "New",
        iterationPath: "VelocyTrack\\Sprint 42",
        url: "https://dev.azure.com/velocytrack/core/_workitems/edit/344",
        projectName: "VelocyTrack-Engineering",
        organization: "Demo",
        reason: "Mention",
        commentCount: 2,
        mentionCount: 1,
        unreadCount: 1,
        isAnswered: false,
        discussionStatus: "AwaitingAnswer",
        latestComment: {
            id: 18,
            workItemId: 344,
            authorName: "Marcus Vance",
            authorEmail: "marcus.vance@demo.velocytrack.com",
            createdDate: new Date(Date.now() - 3600000 * 1).toISOString(),
            text: "@Alex Morgan Can you verify the failover quorum config before we trigger cluster testing?",
            mentionsMe: true,
            isFromCurrentUser: false
        },
        lastActivityUtc: new Date(Date.now() - 3600000 * 1).toISOString()
    },
    {
        workItemId: 273,
        title: "Memory leak in SignalR connection pool under high churn",
        workItemType: "Bug",
        workItemTypeIcon: "bug",
        state: "Active",
        iterationPath: "VelocyTrack\\Sprint 41",
        url: "https://dev.azure.com/velocytrack/core/_workitems/edit/273",
        projectName: "VelocyTrack-Engineering",
        organization: "Demo",
        reason: "Both",
        commentCount: 5,
        mentionCount: 1,
        unreadCount: 0,
        isAnswered: false,
        discussionStatus: "AwaitingReply",
        latestComment: {
            id: 15,
            workItemId: 273,
            authorName: "Alex Morgan",
            authorEmail: "alex.morgan@demo.velocytrack.com",
            createdDate: new Date(Date.now() - 3600000 * 7).toISOString(),
            text: "@Sarah Chen Load tested with 5,000 reconnects; memory consumption now remains stable at ~180MB. Please verify on staging.",
            mentionsMe: false,
            isFromCurrentUser: true
        },
        lastActivityUtc: new Date(Date.now() - 3600000 * 7).toISOString()
    },
    {
        workItemId: 210,
        title: "Real-time Telemetry Ingestion & WebSocket Streaming",
        workItemType: "User Story",
        workItemTypeIcon: "story",
        state: "In Progress",
        iterationPath: "VelocyTrack\\Sprint 41",
        url: "https://dev.azure.com/velocytrack/core/_workitems/edit/210",
        projectName: "VelocyTrack-Engineering",
        organization: "Demo",
        reason: "Both",
        commentCount: 2,
        mentionCount: 1,
        unreadCount: 0,
        isAnswered: true,
        discussionStatus: "Replied",
        latestComment: {
            id: 2,
            workItemId: 210,
            authorName: "Alex Morgan",
            authorEmail: "alex.morgan@demo.velocytrack.com",
            createdDate: new Date(Date.now() - 3600000 * 18).toISOString(),
            text: "Agreed. MessagePack hub protocol package is added to WebApi. Initial benchmarks show ~40% reduction in serialization overhead.",
            mentionsMe: false,
            isFromCurrentUser: true
        },
        lastActivityUtc: new Date(Date.now() - 3600000 * 18).toISOString()
    },
    {
        workItemId: 220,
        title: "OAuth 2.0 Integration for External Platform Connections",
        workItemType: "User Story",
        workItemTypeIcon: "story",
        state: "Active",
        iterationPath: "VelocyTrack\\Sprint 41",
        url: "https://dev.azure.com/velocytrack/core/_workitems/edit/220",
        projectName: "VelocyTrack-Engineering",
        organization: "Demo",
        reason: "Both",
        commentCount: 3,
        mentionCount: 1,
        unreadCount: 0,
        isAnswered: true,
        discussionStatus: "Answered",
        latestComment: {
            id: 6,
            workItemId: 220,
            authorName: "Alex Morgan",
            authorEmail: "alex.morgan@demo.velocytrack.com",
            createdDate: new Date(Date.now() - 86400000 * 2).toISOString(),
            text: "AES-256 GCM encryption service is wired up to secure stored tokens in SQL Server.",
            mentionsMe: false,
            isFromCurrentUser: true
        },
        lastActivityUtc: new Date(Date.now() - 86400000 * 2).toISOString()
    },
    {
        workItemId: 233,
        title: "Chart.js component for sprint commitment vs actual delivery",
        workItemType: "Task",
        workItemTypeIcon: "task",
        state: "Done",
        iterationPath: "VelocyTrack\\Sprint 40",
        url: "https://dev.azure.com/velocytrack/core/_workitems/edit/233",
        projectName: "VelocyTrack-Engineering",
        organization: "Demo",
        reason: "Assigned",
        commentCount: 2,
        mentionCount: 0,
        unreadCount: 0,
        isAnswered: true,
        discussionStatus: "Answered",
        latestComment: {
            id: 10,
            workItemId: 233,
            authorName: "Alex Morgan",
            authorEmail: "alex.morgan@demo.velocytrack.com",
            createdDate: new Date(Date.now() - 86400000 * 3).toISOString(),
            text: "Tested with high-DPI displays; canvas scaling and tooltips look crisp.",
            mentionsMe: false,
            isFromCurrentUser: true
        },
        lastActivityUtc: new Date(Date.now() - 86400000 * 3).toISOString()
    },
    {
        workItemId: 271,
        title: "Timezone offset causes 1-day shift in burndown chart",
        workItemType: "Bug",
        workItemTypeIcon: "bug",
        state: "Closed",
        iterationPath: "VelocyTrack\\Sprint 40",
        url: "https://dev.azure.com/velocytrack/core/_workitems/edit/271",
        projectName: "VelocyTrack-Engineering",
        organization: "Demo",
        reason: "Mention",
        commentCount: 2,
        mentionCount: 1,
        unreadCount: 0,
        isAnswered: true,
        discussionStatus: "Answered",
        latestComment: {
            id: 12,
            workItemId: 271,
            authorName: "Alex Morgan",
            authorEmail: "alex.morgan@demo.velocytrack.com",
            createdDate: new Date(Date.now() - 86400000 * 4).toISOString(),
            text: "Fixed by normalizing all sprint dates to UTC midnight before day grouping.",
            mentionsMe: false,
            isFromCurrentUser: true
        },
        lastActivityUtc: new Date(Date.now() - 86400000 * 4).toISOString()
    }
];

export default {
    name: 'PageDiscussions',
    components: {
        'card-missing-source': CardMissingSource,
        'grid-header-banner': GridHeaderBanner,
        'modal-task-columns': ModalTaskColumns,
        'grid-shell': GridShell,
        'modal-work-item-comments': ModalWorkItemComments,
        'nav-link-button': NavLinkButton,
        'picker-sprints': PickerSprints,
        'status-badge': StatusBadge
    },
    mixins: [mixinToast, commonMixin, accountMixin],
    data() {
        return {
            loadingInbox: true,
            markingAll: false,
            inboxFeed: [],
            selectedDays: 14,
            draftDays: 14,
            selectedSource: 'All',
            draftSource: 'All',
            sourceTypeFilter: 'All',
            searchQuery: '',
            showPrCommentsModal: false,
            selectedPrDiscussion: null,
            prCommentThreads: [],
            loadingPrComments: false,
            prReplyDrafts: {},
            submittingPrReplyThreadId: null,
            updatingPrStatusThreadId: null,
            editingPrReplyKey: null,
            editingPrReplyText: '',
            savingPrReplyKey: null,
            deletingPrReplyKey: null,
            unansweredOnly: false,
            unreadOnly: false,
            allTimeFilter: false,
            selectedSprint: null,
            selectedSprintPath: null,
            hasSprints: false,
            feedAbortController: null,
            settingsPromise: null,
            summary: {
                totalItems: 0,
                unreadCount: 0,
                unansweredMentionCount: 0,
                awaitingAnswerCount: 0,
                awaitingReplyCount: 0
            },
            defaultDiscussionsColumns: DEFAULT_DISCUSSIONS_COLUMNS,
            activeGridColumns: this.loadStoredDiscussionsColumns(),
            discussionsFeatureSettings: this.loadStoredDiscussionsFeatureSettings(),
            gridColumns: [
                { key: 'id', label: '#', headerClass: 'text-center', width: '6.25rem' },
                { key: 'title', label: 'Work Item', headerClass: 'text-left', width: 'minmax(18rem, 2.5fr)' },
                { key: 'status', label: 'Status', headerClass: 'text-center', width: '9.75rem' },
                { key: 'latestComment', label: 'Latest Discussion', headerClass: 'text-left', width: 'minmax(13rem, 1.2fr)' },
                { key: 'actions', label: 'Actions', headerClass: 'text-center', width: '6.5rem' }
            ]
        };
    },
    computed: {
        discussionsFeatureConfig() {
            const cfg = this.discussionsFeatureSettings || {};
            return {
                title: 'Discussions Attention & Inbox Signals',
                subtitle: 'Configure how awaiting-answer highlights, unread envelope indicators, and trigger badges appear in your Discussions inbox.',
                icon: 'bi bi-chat-left-text-fill',
                masterLabel: 'Enable Discussion Signals',
                masterTestId: 'toggle-discussions-features-master',
                storageKey: 'vt_discussions_feature_config',
                enabled: cfg.enabled !== false,
                items: [
                    {
                        key: 'highlightAwaitingAnswer',
                        label: 'Highlight "Awaiting Answer" Rows',
                        badgeText: 'Awaiting Answer',
                        badgeVariant: 'rose',
                        badgeIcon: 'bi bi-exclamation-circle-fill',
                        description: 'Emphasize discussion rows with a red accent border when a teammate mentioned you or replied last.',
                        testId: 'toggle-discussions-awaiting-answer',
                        enabled: cfg.highlightAwaitingAnswer !== false
                    },
                    {
                        key: 'showUnreadEnvelope',
                        label: 'Unread Envelope Indicator',
                        badgeText: 'Unread',
                        badgeVariant: 'sky',
                        badgeIcon: 'bi bi-envelope-fill',
                        description: 'Show an unread envelope icon next to the status badge for threads with unread activity.',
                        testId: 'toggle-discussions-unread-envelope',
                        enabled: cfg.showUnreadEnvelope !== false
                    },
                    {
                        key: 'showReasonBadges',
                        label: 'Inline Trigger & Comment Badges',
                        badgeText: '@Mention / Assigned',
                        badgeVariant: 'amber',
                        badgeIcon: 'bi bi-at',
                        description: 'Display @Mention, Assigned, and comment count pills beneath the work item title.',
                        testId: 'toggle-discussions-reason-badges',
                        enabled: cfg.showReasonBadges !== false
                    }
                ]
            };
        },
        visibleGridColumns() {
            const cols = (this.activeGridColumns && this.activeGridColumns.length)
                ? this.activeGridColumns
                : this.defaultDiscussionsColumns;
            return cols.filter(c => !c.hidden && c.isVisible !== false);
        },
        discussionsStats() {
            const awaitingAnswer = this.summary.awaitingAnswerCount !== undefined
                ? this.summary.awaitingAnswerCount
                : (this.summary.unansweredMentionCount || 0);
            const awaitingReply = this.summary.awaitingReplyCount || 0;
            const unread = this.summary.unreadCount || 0;

            const stats = [
                {
                    key: 'awaiting-answer',
                    label: 'Awaiting Answer',
                    count: awaitingAnswer,
                    icon: 'bi bi-exclamation-circle-fill',
                    variant: 'rose',
                    testId: 'stat-awaiting-answer',
                    showZero: true,
                    tooltip: 'Conversations with questions or mentions awaiting your response'
                }
            ];

            if (awaitingReply > 0) {
                stats.push({
                    key: 'awaiting-reply',
                    label: 'Awaiting Reply',
                    count: awaitingReply,
                    icon: 'bi bi-hourglass-split',
                    variant: 'amber',
                    testId: 'stat-awaiting-reply',
                    tooltip: 'Conversations where you are waiting on replies'
                });
            }

            stats.push({
                key: 'unread',
                label: 'Unread',
                count: unread,
                icon: 'bi bi-bell-fill',
                variant: 'sky',
                testId: 'stat-unread-count',
                showZero: true,
                tooltip: 'Unread discussion updates'
            });

            return stats;
        },
        hasVisibleSprints() {
            return this.hasSprints || (this.configuredProjects && this.configuredProjects.length > 1);
        },
        isMissingBacklogVisible() {
            if (!this.isAuthenticated) return false;
            return !this.hasBacklogSetup;
        },
        hasUnappliedFilters() {
            return this.draftDays !== this.selectedDays || this.draftSource !== this.selectedSource;
        },
        isAnyFilterActive() {
            return (
                this.selectedDays !== 14 ||
                this.selectedSource !== 'All' ||
                this.allTimeFilter ||
                !!this.searchQuery.trim() ||
                this.unansweredOnly ||
                this.unreadOnly ||
                this.sourceTypeFilter !== 'All'
            );
        },
        displayedItems() {
            let list = [...this.inboxFeed];

            // Source filter (client reflection of server source or further narrowed)
            if (this.selectedSource === 'Mention') {
                list = list.filter(i => i.reason === 'Mention' || i.reason === 'Both');
            } else if (this.selectedSource === 'Assigned') {
                list = list.filter(i => i.reason === 'Assigned' || i.reason === 'Both');
            }

            // Sprint filter: if not all time and a sprint is selected, filter by sprint iterationPath
            if (!this.allTimeFilter && this.selectedSprintPath) {
                list = list.filter(i => {
                    if (!i.iterationPath) return false;
                    const path = i.iterationPath.toLowerCase();
                    const target = this.selectedSprintPath.toLowerCase();
                    const sprintName = (this.selectedSprint?.name || '').toLowerCase();
                    return path === target ||
                           path.endsWith('\\' + target) ||
                           (sprintName && path.endsWith(sprintName));
                });
            }

            // View filters
            if (this.sourceTypeFilter === 'WorkItem') {
                list = list.filter(i => (i.sourceType || 'WorkItem') !== 'PullRequest');
            } else if (this.sourceTypeFilter === 'PullRequest') {
                list = list.filter(i => i.sourceType === 'PullRequest');
            }

            if (this.unansweredOnly) {
                list = list.filter(i => i.discussionStatus === 'AwaitingAnswer');
            }
            if (this.unreadOnly) {
                list = list.filter(i => i.unreadCount > 0);
            }

            // Instant search
            const q = this.searchQuery.trim().toLowerCase();
            if (q) {
                list = list.filter(i => {
                    const idMatch = i.workItemId.toString().includes(q);
                    const titleMatch = i.title && i.title.toLowerCase().includes(q);
                    const authorMatch = i.latestComment && i.latestComment.authorName && i.latestComment.authorName.toLowerCase().includes(q);
                    const textMatch = i.latestComment && i.latestComment.text && i.latestComment.text.toLowerCase().includes(q);
                    const repoMatch = i.repositoryName && i.repositoryName.toLowerCase().includes(q);
                    const fileMatch = i.filePath && i.filePath.toLowerCase().includes(q);
                    return idMatch || titleMatch || authorMatch || textMatch || repoMatch || fileMatch;
                });
            }

            return list;
        },
        emptyTitle() {
            if (this.isAnyFilterActive) {
                return 'No discussions match your filters';
            }
            return 'No discussions found';
        },
        emptyMessage() {
            if (this.isAnyFilterActive) {
                return 'Try broadening your search query, turning off view toggles, or switching to All Time.';
            }
            return 'No recent mentions or discussions found in the active project within your lookback window.';
        },
        emptySuggestions() {
            const list = [];
            if (!this.allTimeFilter) list.push('Switch to "All Time" to view discussions across all sprints');
            if (this.searchQuery) list.push('Clear the search query');
            if (this.unansweredOnly) list.push('Disable "Awaiting Answer" toggle');
            if (this.unreadOnly) list.push('Disable "Unread" toggle');
            if (this.sourceTypeFilter !== 'All') list.push('Switch source type to "All"');
            if (this.selectedSource !== 'All') list.push('Switch source to "All Discussions"');
            if (this.selectedDays < 30) list.push('Expand period to 30 days');
            return list;
        }
    },
    watch: {
        async globalProjectId(newVal, oldVal) {
            if (newVal !== oldVal && !this.hasConnectedPlatform) {
                await this.fetchFeed();
            }
        }
    },
    mounted() {
        this.settingsPromise = this.loadInboxSettings();
        if (!this.hasConnectedPlatform) {
            this.settingsPromise.then(() => this.fetchFeed());
        }
    },
    methods: {
        loadStoredDiscussionsFeatureSettings() {
            const defaults = {
                enabled: true,
                highlightAwaitingAnswer: true,
                showUnreadEnvelope: true,
                showReasonBadges: true
            };
            try {
                if (typeof localStorage !== 'undefined') {
                    const raw = localStorage.getItem('vt_discussions_feature_config');
                    if (raw) {
                        const parsed = JSON.parse(raw);
                        if (parsed && typeof parsed === 'object') {
                            return {
                                enabled: parsed.enabled !== false,
                                highlightAwaitingAnswer: parsed.highlightAwaitingAnswer !== false,
                                showUnreadEnvelope: parsed.showUnreadEnvelope !== false,
                                showReasonBadges: parsed.showReasonBadges !== false
                            };
                        }
                    }
                }
            } catch (e) {
                // ignore
            }
            return defaults;
        },
        onDiscussionsFeatureSettingsSaved(newSettings) {
            if (!newSettings || typeof newSettings !== 'object') return;
            this.discussionsFeatureSettings = {
                enabled: newSettings.enabled !== false,
                highlightAwaitingAnswer: newSettings.highlightAwaitingAnswer !== false,
                showUnreadEnvelope: newSettings.showUnreadEnvelope !== false,
                showReasonBadges: newSettings.showReasonBadges !== false
            };
        },
        loadStoredDiscussionsColumns() {
            try {
                if (typeof localStorage !== 'undefined') {
                    const raw = localStorage.getItem('vt_discussions_columns_config');
                    if (raw) {
                        const parsed = JSON.parse(raw);
                        if (Array.isArray(parsed) && parsed.length > 0) {
                            return parsed.map((c, idx) => {
                                const def = DEFAULT_DISCUSSIONS_COLUMNS.find(d => d.referenceName === (c.referenceName || c.key)) || {};
                                return {
                                    ...def,
                                    ...c,
                                    key: c.key || c.referenceName || def.key,
                                    referenceName: c.referenceName || c.key || def.referenceName,
                                    headerClass: c.headerClass || def.headerClass || 'text-center',
                                    width: c.width || def.width || 'minmax(6rem, 1fr)',
                                    order: c.order || idx + 1,
                                    isVisible: c.isVisible !== undefined ? !!c.isVisible : !c.hidden,
                                    hidden: c.hidden !== undefined ? !!c.hidden : c.isVisible === false
                                };
                            });
                        }
                    }
                }
            } catch (e) {
                // ignore
            }
            return DEFAULT_DISCUSSIONS_COLUMNS.map(c => ({
                ...c,
                allowedValues: c.allowedValues ? [...c.allowedValues] : [],
                valueColors: c.valueColors ? { ...c.valueColors } : {}
            }));
        },
        openColumnSettings() {
            if (this.$refs.modalDiscussionsColumns) {
                this.$refs.modalDiscussionsColumns.showModal();
            }
        },
        onColumnsSaved(newColumns) {
            if (Array.isArray(newColumns) && newColumns.length > 0) {
                this.activeGridColumns = newColumns.map((c, idx) => {
                    const def = DEFAULT_DISCUSSIONS_COLUMNS.find(d => d.referenceName === (c.referenceName || c.key)) || {};
                    return {
                        ...def,
                        ...c,
                        key: c.key || c.referenceName || def.key,
                        referenceName: c.referenceName || c.key || def.referenceName,
                        headerClass: c.headerClass || def.headerClass || 'text-center',
                        width: c.width || def.width || 'minmax(6rem, 1fr)',
                        order: c.order || idx + 1,
                        isVisible: c.isVisible !== undefined ? !!c.isVisible : !c.hidden,
                        hidden: c.hidden !== undefined ? !!c.hidden : c.isVisible === false
                    };
                });
            }
        },
        discussionStatusStyle(statusKey) {
            if (!statusKey) return {};
            const statusCol = (this.activeGridColumns || []).find(c => (c.referenceName || c.key) === 'status');
            if (statusCol && statusCol.valueColors && typeof statusCol.valueColors === 'object') {
                const hex = statusCol.valueColors[statusKey] ||
                    (Object.entries(statusCol.valueColors).find(([k]) => k.toLowerCase() === statusKey.toLowerCase()) || [])[1];
                if (hex && hex !== DEFAULT_DISCUSSION_STATUS_COLORS[statusKey]) {
                    return {
                        backgroundColor: `${hex} !important`,
                        color: '#ffffff !important',
                        borderColor: `${hex} !important`
                    };
                }
            }
            return {};
        },
        formatIterationLeaf(path) {
            if (!path) return '—';
            const parts = String(path).split(/[\\/]/).filter(Boolean);
            return parts.length ? parts[parts.length - 1] : path;
        },
        async loadInboxSettings() {
            try {
                const cfgResp = await this.$http.get('/Config/GetSettings');
                if (cfgResp?.data?.inboxAssignedDays) {
                    const days = parseInt(cfgResp.data.inboxAssignedDays, 10);
                    if (days >= 1 && days <= 30) {
                        this.selectedDays = days;
                        this.draftDays = days;
                    }
                }
            } catch (_) {
                // Keep default 14
            }
        },
        async initSettingsAndFetch() {
            this.loadingInbox = true;
            try {
                const cfgResp = await this.$http.get('/Config/GetSettings');
                if (cfgResp?.data?.inboxAssignedDays) {
                    const days = parseInt(cfgResp.data.inboxAssignedDays, 10);
                    if (days >= 1 && days <= 30) {
                        this.selectedDays = days;
                        this.draftDays = days;
                    }
                }
            } catch (_) {
                // Keep default 14
            }
            this.fetchFeed();
        },
        cloneMockDiscussions() {
            return JSON.parse(JSON.stringify(MOCK_DISCUSSIONS));
        },
        async fetchFeed() {
            if (this.feedAbortController) {
                this.feedAbortController.abort();
                this.feedAbortController = null;
            }
            const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
            this.feedAbortController = controller;
            this.loadingInbox = true;
            try {
                const params = { days: this.selectedDays };
                if (!this.allTimeFilter && this.selectedSprintPath) {
                    params.sprint = this.selectedSprintPath;
                } else if (this.allTimeFilter) {
                    params.allTime = true;
                }
                const resp = await this.$http.get('/Inbox/GetFeed', {
                    params,
                    signal: controller ? controller.signal : undefined
                });
                if (Array.isArray(resp.data)) {
                    this.inboxFeed = resp.data;
                } else if (this.isDemo) {
                    this.inboxFeed = this.cloneMockDiscussions();
                } else {
                    this.inboxFeed = [];
                }
                this.applyDismissedStates();
                this.updateSummaryFromFeed();
            } catch (err) {
                if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError') {
                    return;
                }
                if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) return;
                if (this.isDemo) {
                    this.inboxFeed = this.cloneMockDiscussions();
                    this.applyDismissedStates();
                    this.updateSummaryFromFeed();
                } else {
                    this.inboxFeed = [];
                    this.updateSummaryFromFeed();
                }
            } finally {
                if (!controller || this.feedAbortController === controller) {
                    this.loadingInbox = false;
                    this.feedAbortController = null;
                }
            }
        },
        async fetchFeedLegacyUnused() {
            this.loadingInbox = true;
            try {
                const resp = await this.$http.get('/Inbox/GetFeed', {
                    params: { days: this.selectedDays }
                });
                if (Array.isArray(resp.data) && resp.data.length > 0) {
                    this.inboxFeed = resp.data;
                } else if (this.isDemo) {
                    this.inboxFeed = this.cloneMockDiscussions();
                } else {
                    this.inboxFeed = Array.isArray(resp.data) ? resp.data : [];
                }
                this.applyDismissedStates();
                this.updateSummaryFromFeed();
            } catch (err) {
                if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) return;
                if (this.isDemo) {
                    this.inboxFeed = this.cloneMockDiscussions();
                    this.applyDismissedStates();
                    this.updateSummaryFromFeed();
                } else {
                    this.inboxFeed = [];
                    this.updateSummaryFromFeed();
                }
            } finally {
                this.loadingInbox = false;
            }
        },
        updateSummaryFromFeed() {
            const total = this.inboxFeed.length;
            const unread = this.inboxFeed.filter(i => i.unreadCount > 0).length;
            const awaitingAnswer = this.inboxFeed.filter(i => i.discussionStatus === 'AwaitingAnswer').length;
            const awaitingReply = this.inboxFeed.filter(i => i.discussionStatus === 'AwaitingReply').length;
            this.summary = {
                totalItems: total,
                unreadCount: unread,
                unansweredMentionCount: awaitingAnswer,
                awaitingAnswerCount: awaitingAnswer,
                awaitingReplyCount: awaitingReply
            };
            this.$root.$emit('inbox-summary-updated', this.summary);
        },
        applyFilters() {
            this.selectedDays = this.draftDays;
            this.selectedSource = this.draftSource;
            this.fetchFeed();
        },
        resetAllFilters() {
            this.draftDays = 14;
            this.selectedDays = 14;
            this.draftSource = 'All';
            this.selectedSource = 'All';
            this.searchQuery = '';
            this.unansweredOnly = false;
            this.unreadOnly = false;
            this.sourceTypeFilter = 'All';
            this.allTimeFilter = false;
            this.fetchFeed();
        },
        async onSprintPicked(sprintPath, sprint, project, isAllTime = false) {
            if (isAllTime) {
                this.allTimeFilter = true;
                this.selectedSprint = null;
                this.selectedSprintPath = null;
            } else {
                this.allTimeFilter = false;
                this.selectedSprint = sprint;
                this.selectedSprintPath = sprintPath;
                if (sprintPath) {
                    this.hasSprints = true;
                }
            }
            if (this.settingsPromise) {
                await this.settingsPromise;
            }
            await this.fetchFeed();
        },
        onSprintPickedLegacyUnused(sprintPath, sprint, project, isAllTime = false) {
            if (isAllTime) {
                this.allTimeFilter = true;
                this.selectedSprint = null;
                this.selectedSprintPath = null;
            } else {
                this.allTimeFilter = false;
                this.selectedSprint = sprint;
                this.selectedSprintPath = sprintPath;
                if (sprintPath) {
                    this.hasSprints = true;
                }
            }
        },
        onSprintsLoaded(sprints) {
            this.hasSprints = Array.isArray(sprints) && sprints.length > 0;
        },
        sourceLabel(source) {
            switch (source) {
                case 'Mention': return 'Mentioned Me';
                case 'Assigned': return 'Assigned to Me';
                default: return 'All Discussions';
            }
        },
        openDiscussion(item) {
            if (!this.$refs.commentsModal) return;
            if (item && item.sourceType === 'PullRequest') {
                this.openPrDiscussionModal(item);
                return;
            }
            if (!this.$refs.commentsModal) return;
            this.$refs.commentsModal.open(item);
        },
        hasMentionToOthers(text) {
            if (!text) return false;
            if (text.includes('data-vss-mention')) return true;
            const matches = text.match(/(?:^|\s)@([a-zA-Z0-9_.-]+(?:\s+[a-zA-Z0-9_.-]+)?)/g);
            return !!(matches && matches.length > 0);
        },
        calculateDiscussionStatus(comments) {
            if (!comments || comments.length === 0) return 'NotActionable';
            const mentionsToMe = comments.filter(c => c.mentionsMe);
            const mentionsFromMe = comments.filter(c => c.isFromCurrentUser && this.hasMentionToOthers(c.text));

            const lastMentionToMe = mentionsToMe[mentionsToMe.length - 1];
            const lastMentionFromMe = mentionsFromMe[mentionsFromMe.length - 1];

            const isAwaitingAnswer = !!lastMentionToMe && !comments.some(c => c.isFromCurrentUser && new Date(c.createdDate) > new Date(lastMentionToMe.createdDate));
            const isAwaitingReply = !!lastMentionFromMe && !comments.some(c => !c.isFromCurrentUser && new Date(c.createdDate) > new Date(lastMentionFromMe.createdDate));

            if (isAwaitingAnswer) return 'AwaitingAnswer';
            if (isAwaitingReply) return 'AwaitingReply';
            if (lastMentionToMe || lastMentionFromMe) {
                if (lastMentionFromMe && (!lastMentionToMe || new Date(lastMentionFromMe.createdDate) >= new Date(lastMentionToMe.createdDate))) {
                    return 'Replied';
                }
                return 'Answered';
            }
            return 'NotActionable';
        },
        onCommentsLoaded({ workItemId, comments }) {
            const item = this.inboxFeed.find(i => i.workItemId === workItemId);
            if (item && Array.isArray(comments)) {
                item.commentCount = comments.length;
                item.mentionCount = comments.filter(c => c.mentionsMe).length;
                if (comments.length > 0) {
                    item.latestComment = comments[0];
                    const record = this.$store.state.dismissedDiscussions?.[item.workItemId];
                    if (record && item.latestComment.id <= record.lastCommentId) {
                        item.discussionStatus = 'NotActionable';
                        item.isAnswered = true;
                    } else {
                        item.discussionStatus = this.calculateDiscussionStatus([...comments].reverse());
                        item.isAnswered = (item.discussionStatus !== 'AwaitingAnswer' && item.discussionStatus !== 'AwaitingReply');
                    }
                } else {
                    item.discussionStatus = 'NotActionable';
                    item.isAnswered = true;
                }
                item.unreadCount = 0;
                this.updateSummaryFromFeed();
            }
        },
        dismissStatus(item, showToast = true) {
            if (!item) return;
            const lastCommentId = item.latestComment?.id || 0;
            this.$set(item, 'discussionStatus', 'NotActionable');
            this.$set(item, 'isAnswered', true);

            this.$store.commit('dismissDiscussion', {
                workItemId: item.workItemId,
                lastCommentId
            });

            this.updateSummaryFromFeed();
            if (showToast) {
                this.toastSuccess('Label dismissed', `Marked discussion #${item.workItemId} as resolved in person/meeting.`);
            }
        },
        onStatusDismissed({ workItemId }) {
            const item = this.inboxFeed.find(i => i.workItemId === workItemId);
            if (item) {
                this.dismissStatus(item, false);
            }
        },
        applyDismissedStates() {
            const dismissedMap = this.$store.state.dismissedDiscussions || {};
            for (const item of this.inboxFeed) {
                const record = dismissedMap[item.workItemId];
                if (record) {
                    const latestId = item.latestComment?.id || 0;
                    if (latestId <= record.lastCommentId) {
                        item.discussionStatus = 'NotActionable';
                        item.isAnswered = true;
                    }
                }
            }
        },
        onCommentAdded({ workItemId, commentCount, mentionCount, comment }) {
            const item = this.inboxFeed.find(i => i.workItemId === workItemId);
            if (item) {
                if (commentCount !== undefined) item.commentCount = commentCount;
                if (mentionCount !== undefined) item.mentionCount = mentionCount;
                if (comment) item.latestComment = comment;
                item.unreadCount = 0;
                this.$store.commit('undismissDiscussion', workItemId);
                if (item.discussionStatus === 'AwaitingAnswer') {
                    item.discussionStatus = 'Answered';
                } else if (this.hasMentionToOthers(comment?.text)) {
                    item.discussionStatus = 'AwaitingReply';
                }
                item.isAnswered = (item.discussionStatus !== 'AwaitingAnswer');
                this.updateSummaryFromFeed();
            }
        },
        syncThreadFromComments(item, comments, commentCount, mentionCount, latestComment) {
            if (!item) return;
            if (commentCount !== undefined) this.$set(item, 'commentCount', commentCount);
            if (mentionCount !== undefined) this.$set(item, 'mentionCount', mentionCount);
            if (Array.isArray(comments)) {
                this.$set(item, 'commentCount', comments.length);
                this.$set(item, 'mentionCount', comments.filter(c => c.mentionsMe).length);
                if (comments.length > 0) {
                    const newest = { ...comments[0] };
                    this.$set(item, 'latestComment', newest);
                    if (newest.createdDate) {
                        this.$set(item, 'lastActivityUtc', newest.createdDate);
                    }
                    const record = this.$store.state.dismissedDiscussions?.[item.workItemId];
                    if (record && newest.id <= record.lastCommentId) {
                        this.$set(item, 'discussionStatus', 'NotActionable');
                        this.$set(item, 'isAnswered', true);
                    } else {
                        const nextStatus = this.calculateDiscussionStatus([...comments].reverse());
                        this.$set(item, 'discussionStatus', nextStatus);
                        this.$set(item, 'isAnswered', nextStatus !== 'AwaitingAnswer' && nextStatus !== 'AwaitingReply');
                    }
                } else {
                    this.$set(item, 'latestComment', null);
                    this.$set(item, 'discussionStatus', 'NotActionable');
                    this.$set(item, 'isAnswered', true);
                }
            } else if (latestComment !== undefined) {
                this.$set(item, 'latestComment', latestComment ? { ...latestComment } : null);
            }
            this.$set(item, 'unreadCount', 0);
            this.updateSummaryFromFeed();
        },
        onCommentDeleted({ workItemId, commentCount, mentionCount, latestComment, comments }) {
            const item = this.inboxFeed.find(i => i.workItemId === workItemId);
            if (item) {
                this.syncThreadFromComments(item, comments, commentCount, mentionCount, latestComment);
            }
        },
        onCommentsUpdated({ workItemId, commentCount, mentionCount, latestComment, comments }) {
            const item = this.inboxFeed.find(i => i.workItemId === workItemId);
            if (item) {
                this.syncThreadFromComments(item, comments, commentCount, mentionCount, latestComment);
            }
        },
        onCommentsUpdatedLegacyUnused({ workItemId, commentCount, mentionCount }) {
            const item = this.inboxFeed.find(i => i.workItemId === workItemId);
            if (item) {
                if (commentCount !== undefined) item.commentCount = commentCount;
                if (mentionCount !== undefined) item.mentionCount = mentionCount;
                item.unreadCount = 0;
                this.updateSummaryFromFeed();
            }
        },
        async markAllAsRead() {
            if (this.markingAll || this.summary.unreadCount === 0) return;
            this.markingAll = true;
            try {
                const payload = this.inboxFeed.map(i => ({
                    workItemId: i.workItemId,
                    lastCommentId: i.latestComment ? i.latestComment.id : 0
                }));
                await this.$http.post('/Inbox/MarkAllSeen', payload);
                this.inboxFeed.forEach(i => {
                    i.unreadCount = 0;
                });
                this.updateSummaryFromFeed();
                this.toastSuccess('Discussions Updated', 'All discussions marked as read.');
            } catch (err) {
                this.toastError('Failed to mark all as read', err?.response?.data?.message || err.message);
            } finally {
                this.markingAll = false;
            }
        },
        formatRelative(dateStr) {
            if (!dateStr) return '';
            try {
                const d = new Date(dateStr);
                const now = new Date();
                const diffMs = now - d;
                const diffMins = Math.floor(diffMs / 60000);
                if (diffMins < 1) return 'just now';
                if (diffMins < 60) return `${diffMins}m ago`;
                const diffHours = Math.floor(diffMins / 60);
                if (diffHours < 24) return `${diffHours}h ago`;
                const diffDays = Math.floor(diffHours / 24);
                if (diffDays < 7) return `${diffDays}d ago`;
                return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            } catch (_) {
                return dateStr;
            }
        },
        formatExcerpt(text) {
            if (!text) return '';
            const stripped = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
            if (stripped.length <= 120) return stripped;
            return stripped.substring(0, 117) + '...';
        },
        formatFileLeaf(filePath) {
            if (!filePath) return '';
            const parts = String(filePath).split(/[\\/]/).filter(Boolean);
            return parts.length > 0 ? parts[parts.length - 1] : filePath;
        },
        async openPrDiscussionModal(item) {
            if (!item) return;
            const prId = item.pullRequestId || item.workItemId;
            this.selectedPrDiscussion = item;
            this.prCommentThreads = [];
            this.prReplyDrafts = {};
            this.loadingPrComments = true;
            this.showPrCommentsModal = true;

            try {
                const params = {
                    repositoryId: item.repositoryId,
                    pullRequestId: prId
                };
                if (this.globalProjectId) {
                    params.projectId = this.globalProjectId;
                }
                const resp = await this.$http.get('/Azure/ListPullRequestComments', { params });
                this.prCommentThreads = Array.isArray(resp.data) ? resp.data : [];
                this.markPrDiscussionSeen(item);
            } catch (err) {
                this.prCommentThreads = [];
                this.toastError('Failed to load pull request comments.');
            } finally {
                this.loadingPrComments = false;
            }
        },
        async markPrDiscussionSeen(item) {
            if (!item) return;
            this.$set(item, 'unreadCount', 0);
            this.updateSummaryFromFeed();
            try {
                const lastCommentId = item.latestComment ? item.latestComment.id : 1;
                await this.$http.post('/Inbox/MarkSeen', {
                    workItemId: item.workItemId,
                    lastCommentId: Math.max(1, lastCommentId)
                });
            } catch (_) {
                // Non-blocking
            }
        },
        syncPrInboxItemFromThreads() {
            if (!this.selectedPrDiscussion) return;
            const item = this.inboxFeed.find(
                i => i.sourceType === 'PullRequest' && (i.pullRequestId || i.workItemId) === (this.selectedPrDiscussion.pullRequestId || this.selectedPrDiscussion.workItemId)
            ) || this.selectedPrDiscussion;

            const threads = this.prCommentThreads || [];
            if (threads.length === 0) {
                this.$set(item, 'commentCount', 0);
                this.$set(item, 'discussionStatus', 'NotActionable');
                this.$set(item, 'isAnswered', true);
                this.updateSummaryFromFeed();
                return;
            }

            let totalComments = 0;
            let newestMsg = null;
            for (const t of threads) {
                totalComments += 1 + (Array.isArray(t.replies) ? t.replies.length : 0);
                const rootCandidate = {
                    id: t.commentId || 1,
                    workItemId: item.workItemId,
                    authorName: t.authorName,
                    createdDate: t.publishedDate,
                    text: t.text
                };
                if (!newestMsg || new Date(rootCandidate.createdDate) >= new Date(newestMsg.createdDate)) {
                    newestMsg = rootCandidate;
                }
                if (Array.isArray(t.replies)) {
                    for (const r of t.replies) {
                        const replyCandidate = {
                            id: r.commentId || 1,
                            workItemId: item.workItemId,
                            authorName: r.authorName,
                            createdDate: r.publishedDate,
                            text: r.text
                        };
                        if (!newestMsg || new Date(replyCandidate.createdDate) >= new Date(newestMsg.createdDate)) {
                            newestMsg = replyCandidate;
                        }
                    }
                }
            }

            const hasActive = threads.some(t => t.isActive || t.status === 'Active');
            this.$set(item, 'commentCount', totalComments);
            this.$set(item, 'threadStatus', hasActive ? 'Active' : (threads[0]?.status || 'Fixed'));
            if (newestMsg) {
                this.$set(item, 'latestComment', newestMsg);
                this.$set(item, 'lastActivityUtc', newestMsg.createdDate);
            }
            if (!hasActive) {
                this.$set(item, 'discussionStatus', 'Answered');
                this.$set(item, 'isAnswered', true);
            }
            this.$set(item, 'unreadCount', 0);
            this.updateSummaryFromFeed();
        },
        prThreadStatusVariant(status) {
            switch (status) {
                case 'Active': return 'danger';
                case 'Pending':
                case 'WontFix':
                case "Won't Fix": return 'warning';
                case 'Fixed':
                case 'Resolved': return 'success';
                case 'ByDesign': return 'info';
                case 'Closed': return 'secondary';
                default: return 'secondary';
            }
        },
        getPrThreadActionVariant(transition) {
            if (!transition || !transition.variant) return 'outline-secondary';
            return `outline-${transition.variant}`;
        },
        async changePrThreadStatus(thread, transition) {
            if (!this.selectedPrDiscussion || !thread || !transition) return;
            const targetStatus = typeof transition === 'string' ? transition : transition.targetStatus;
            const prId = this.selectedPrDiscussion.pullRequestId || this.selectedPrDiscussion.workItemId;
            this.updatingPrStatusThreadId = thread.threadId;
            try {
                const payload = {
                    repositoryId: this.selectedPrDiscussion.repositoryId,
                    pullRequestId: prId,
                    threadId: thread.threadId,
                    status: targetStatus
                };
                if (this.globalProjectId) {
                    payload.projectId = this.globalProjectId;
                }
                const resp = await this.$http.post('/Azure/UpdatePullRequestThreadStatus', payload);
                if (resp && resp.data) {
                    this.$set(thread, 'status', resp.data.status);
                    this.$set(thread, 'isActive', resp.data.isActive);
                    if (Array.isArray(resp.data.allowedTransitions)) {
                        this.$set(thread, 'allowedTransitions', resp.data.allowedTransitions);
                    }
                    this.syncPrInboxItemFromThreads();
                    this.toastSuccess(`Thread marked as ${resp.data.status}.`);
                }
            } catch (err) {
                this.toastError('Failed to update thread status.');
            } finally {
                this.updatingPrStatusThreadId = null;
            }
        },
        canMutatePrComment(authorName) {
            if (this.isDemo) return true;
            if (!authorName) return false;
            const currentName = (this.userName || '').trim().toLowerCase();
            const currentEmail = (this.userEmail || '').trim().toLowerCase();
            const target = authorName.trim().toLowerCase();
            return (currentName && target === currentName) || (currentEmail && target === currentEmail);
        },
        startEditPrReply(threadId, commentId, currentText) {
            this.editingPrReplyKey = `${threadId}-${commentId}`;
            this.editingPrReplyText = currentText || '';
        },
        cancelEditPrReply() {
            this.editingPrReplyKey = null;
            this.editingPrReplyText = '';
        },
        async saveEditPrReply(thread, commentId, isRoot = false) {
            if (!this.selectedPrDiscussion || !thread) return;
            const text = (this.editingPrReplyText || '').trim();
            if (!text) return;
            const key = `${thread.threadId}-${commentId}`;
            const prId = this.selectedPrDiscussion.pullRequestId || this.selectedPrDiscussion.workItemId;
            this.savingPrReplyKey = key;
            try {
                const payload = {
                    repositoryId: this.selectedPrDiscussion.repositoryId,
                    pullRequestId: prId,
                    threadId: thread.threadId,
                    commentId,
                    text
                };
                if (this.globalProjectId) {
                    payload.projectId = this.globalProjectId;
                }
                const resp = await this.$http.put('/Azure/UpdatePullRequestThreadReply', payload);
                if (resp && resp.data) {
                    if (isRoot) {
                        this.$set(thread, 'text', resp.data.text);
                    } else if (Array.isArray(thread.replies)) {
                        const idx = thread.replies.findIndex(r => r.commentId === commentId);
                        if (idx !== -1) {
                            this.$set(thread.replies, idx, resp.data);
                        }
                    }
                    this.cancelEditPrReply();
                    this.syncPrInboxItemFromThreads();
                    this.toastSuccess('Comment updated.');
                }
            } catch (err) {
                this.toastError('Failed to update comment.');
            } finally {
                this.savingPrReplyKey = null;
            }
        },
        async deletePrReply(thread, commentId) {
            if (!this.selectedPrDiscussion || !thread) return;
            const key = `${thread.threadId}-${commentId}`;
            const prId = this.selectedPrDiscussion.pullRequestId || this.selectedPrDiscussion.workItemId;
            this.deletingPrReplyKey = key;
            try {
                const params = {
                    repositoryId: this.selectedPrDiscussion.repositoryId,
                    pullRequestId: prId,
                    threadId: thread.threadId,
                    commentId
                };
                if (this.globalProjectId) {
                    params.projectId = this.globalProjectId;
                }
                const resp = await this.$http.delete('/Azure/DeletePullRequestThreadReply', { params });
                if (resp && resp.data && resp.data.deleted) {
                    const isRoot = (thread.commentId || 1) === commentId;
                    if (isRoot && (!thread.replies || thread.replies.length === 0)) {
                        this.prCommentThreads = this.prCommentThreads.filter(t => t.threadId !== thread.threadId);
                    } else if (isRoot && thread.replies && thread.replies.length > 0) {
                        const promoted = thread.replies.shift();
                        this.$set(thread, 'commentId', promoted.commentId);
                        this.$set(thread, 'authorName', promoted.authorName);
                        this.$set(thread, 'publishedDate', promoted.publishedDate);
                        this.$set(thread, 'text', promoted.text);
                    } else if (Array.isArray(thread.replies)) {
                        this.$set(thread, 'replies', thread.replies.filter(r => r.commentId !== commentId));
                    }
                    this.syncPrInboxItemFromThreads();
                    this.toastSuccess('Comment deleted.');
                }
            } catch (err) {
                this.toastError('Failed to delete comment.');
            } finally {
                this.deletingPrReplyKey = null;
            }
        },
        async submitPrReply(thread) {
            if (!this.selectedPrDiscussion || !thread) return;
            const text = (this.prReplyDrafts[thread.threadId] || '').trim();
            if (!text) return;
            const prId = this.selectedPrDiscussion.pullRequestId || this.selectedPrDiscussion.workItemId;
            this.submittingPrReplyThreadId = thread.threadId;
            try {
                const payload = {
                    repositoryId: this.selectedPrDiscussion.repositoryId,
                    pullRequestId: prId,
                    threadId: thread.threadId,
                    text
                };
                if (this.globalProjectId) {
                    payload.projectId = this.globalProjectId;
                }
                const resp = await this.$http.post('/Azure/ReplyPullRequestThread', payload);
                if (resp && resp.data) {
                    if (!Array.isArray(thread.replies)) {
                        this.$set(thread, 'replies', []);
                    }
                    thread.replies.push(resp.data);
                    this.$set(this.prReplyDrafts, thread.threadId, '');

                    const item = this.inboxFeed.find(
                        i => i.sourceType === 'PullRequest' && (i.pullRequestId || i.workItemId) === prId
                    ) || this.selectedPrDiscussion;
                    if (item) {
                        if (item.discussionStatus === 'AwaitingAnswer') {
                            this.$set(item, 'discussionStatus', 'Answered');
                            this.$set(item, 'isAnswered', true);
                        } else if (this.hasMentionToOthers(text)) {
                            this.$set(item, 'discussionStatus', 'AwaitingReply');
                            this.$set(item, 'isAnswered', false);
                        }
                    }
                    this.syncPrInboxItemFromThreads();
                    this.toastSuccess('Reply posted to pull request thread.');
                }
            } catch (err) {
                this.toastError('Failed to post reply to pull request thread.');
            } finally {
                this.submittingPrReplyThreadId = null;
            }
        },
        formatPrCommentDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return '';
            return d.toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    }
};
</script>

<style scoped>
.page-discussions,
.page-inbox {
    width: 100%;
}

.discussions-grid-overlay {
    min-height: 280px;
    width: 100%;
    display: block;
}

.inbox-search-input {
    width: 220px;
}

.inbox-item-card {
    background-color: var(--card-bg, #ffffff);
    border-color: var(--border-color, #e9ecef);
    transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
}

.inbox-item-card:hover {
    border-color: var(--primary, #0d6efd);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.inbox-item-card.is-unread {
    border-left: 3px solid #0d6efd !important;
}

.inbox-item-card.needs-reply {
    border-left: 3px solid #dc3545 !important;
}

.reply-status-badge {
    font-size: 0.76rem;
    letter-spacing: 0.02em;
}

.latest-comment-box {
    background: rgba(0, 0, 0, 0.025);
    border: 1px solid rgba(0, 0, 0, 0.05);
}

[data-theme="dark"] .latest-comment-box {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.08);
}

.comment-excerpt {
    color: var(--text-muted, #6c757d);
    line-height: 1.35;
}

.type-icon ::v-deep svg,
.type-icon ::v-deep img {
    width: 15px;
    height: 15px;
    vertical-align: middle;
}

.extra-small {
    font-size: 0.72rem;
}

.grid-col-id {
    overflow: visible;
    position: relative;
}

.badge-mention {
    background-color: rgba(255, 193, 7, 0.22) !important;
    color: #856404 !important;
    border: 1px solid rgba(255, 193, 7, 0.5) !important;
    font-weight: 700 !important;
}

[data-theme="dark"] .badge-mention {
    background-color: rgba(255, 193, 7, 0.25) !important;
    color: #ffda6a !important;
    border: 1px solid rgba(255, 193, 7, 0.5) !important;
}

.unread-envelope-icon {
    font-size: 1.05rem;
    vertical-align: middle;
}

.badge-dismiss-btn {
    background: transparent;
    border: none;
    padding: 0;
    margin: 0;
    line-height: 1;
    font-size: 0.9rem;
    color: inherit;
    opacity: 0.7;
    cursor: pointer;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.15s, background-color 0.15s;
    width: 14px;
    height: 14px;
}

.badge-dismiss-btn:hover {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.2);
}

[data-theme="dark"] .badge-dismiss-btn:hover {
    background-color: rgba(255, 255, 255, 0.25);
}

.mobile-col-caption {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-muted, #6c757d);
    margin-bottom: 0.2rem;
}

@media (max-width: 991.98px) {
    .inbox-search-input {
        width: 100%;
        max-width: 100%;
        margin-bottom: 0.25rem;
    }

    .inbox-item-card {
        border-radius: 0.65rem !important;
        padding: 0.75rem 0.85rem !important;
        background: rgba(30, 41, 59, 0.65) !important;
        border: 1px solid rgba(255, 255, 255, 0.08) !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18) !important;
        margin-bottom: 0.65rem !important;
    }

    [data-theme="light"] .inbox-item-card {
        background: #ffffff !important;
        border: 1px solid rgba(0, 0, 0, 0.08) !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
    }

    .inbox-item-card.is-unread {
        border-left: 3px solid #0d6efd !important;
    }

    .inbox-item-card.needs-reply {
        border-left: 3px solid #dc3545 !important;
    }

    .inbox-item-card .card-body {
        padding: 0 !important;
    }

    .inbox-item-card .grid-row {
        display: flex !important;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.45rem 0.6rem;
        padding: 0 !important;
        margin: 0 !important;
    }

    .inbox-item-card .grid-col-id,
    .inbox-item-card .grid-col-status {
        order: 1 !important;
        flex: 0 0 auto !important;
        width: auto !important;
        padding: 0 !important;
    }

    .inbox-item-card .grid-col-actions {
        order: 1 !important;
        margin-left: auto !important;
        flex: 0 0 auto !important;
        width: auto !important;
        padding: 0 !important;
    }

    .inbox-item-card .grid-col-title {
        order: 2 !important;
        flex: 0 0 100% !important;
        width: 100% !important;
        max-width: 100% !important;
        padding: 0 !important;
        font-size: 0.95rem;
        line-height: 1.35;
    }

    .inbox-item-card .grid-col-latestComment {
        order: 3 !important;
        flex: 0 0 100% !important;
        width: 100% !important;
        max-width: 100% !important;
        padding: 0 !important;
    }
}

.discussions-source-pills .btn {
    font-size: 0.76rem;
    padding: 0.2rem 0.55rem;
}

.badge-pr-repo,
.badge-pr-file-path {
    background-color: rgba(13, 110, 253, 0.08) !important;
    color: var(--text-main, #212529) !important;
    border-color: rgba(13, 110, 253, 0.22) !important;
}

[data-theme="dark"] .badge-pr-repo,
[data-theme="dark"] .badge-pr-file-path {
    background-color: rgba(56, 189, 248, 0.12) !important;
    color: #e2e8f0 !important;
    border-color: rgba(56, 189, 248, 0.28) !important;
}

.pr-comment-thread {
    background-color: var(--card-bg, #ffffff);
    border-color: var(--border-color, #dee2e6) !important;
}

.pr-comment-thread-active {
    border-left: 3px solid #ffc107 !important;
}

[data-theme="dark"] .pr-comment-thread {
    background-color: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.1) !important;
}

.discussions-mobile-noop {
}
</style>
